import React, { useState, useEffect } from 'react';
import styles from './SimpleUserForm.module.css';
import { X, User, Mail, Phone, Lock, Shield, Users, Home } from 'lucide-react';
import { addUserManager } from '../../Fetch/fetchUser';

const SimpleUserForm = ({ onSave, onCancel, forceManager = false }) => {
  const [formData, setFormData] = useState({
    FullName: '',
    Email: '',
    Phone: '',
    PasswordHash: '',
    IsManager: 'כן',  // Default to manager for new users
    IsVaad: 'לא',
    IsTenant: 'כן'     // Default to tenant for new users
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Force clear email field on component mount and after render to prevent autofill
  useEffect(() => {
    // Clear immediately
    setFormData(prev => ({
      ...prev,
      Email: ''
    }));

    // Also clear after a short delay to override browser autofill
    const timer = setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        Email: ''
      }));
      
      // Also clear the DOM element directly
      const emailInput = document.getElementById('userEmailField');
      if (emailInput) {
        emailInput.value = '';
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Get building ID from localStorage
  const getBuildingId = () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    return userData?.buildingId;
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.FullName?.trim()) {
      newErrors.FullName = 'שם מלא הוא שדה חובה';
    }

    if (!formData.Email?.trim()) {
      newErrors.Email = 'אימייל הוא שדה חובה';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) {
      newErrors.Email = 'אימייל לא תקין';
    }

    if (!formData.Phone?.trim()) {
      newErrors.Phone = 'טלפון הוא שדה חובה';
    } else if (!/^[0-9-]+$/.test(formData.Phone.replace(/\s/g, ''))) {
      newErrors.Phone = 'מספר טלפון לא תקין';
    }

    return newErrors;
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked ? 'כן' : 'לא'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      const buildingId = getBuildingId();
      
      if (!buildingId) {
        alert('לא נמצא מזהה בניין');
        setLoading(false);
        return;
      }

      const payload = {
        ...formData,
        BuildingId: buildingId
      };

      // If password is empty, don't send it (backend will generate)
      if (!payload.PasswordHash) {
        delete payload.PasswordHash;
      }

      const data = await addUserManager(payload);

      // Show success message
      let successMessage = data.message || 'המשתמש נוסף בהצלחה';
      
      if (data.TemporaryPassword) {
        successMessage += `\n\nסיסמה זמנית: ${data.TemporaryPassword}\n\nאנא שמור את הסיסמה ושלח אותה למשתמש`;
      }

      alert(successMessage);

      // Trigger refresh event
      window.dispatchEvent(new Event('userDataChanged'));

      // Call parent callback
      onSave(data);

    } catch (error) {
      console.error('Error adding user:', error);
      alert(error.message || 'שגיאה בהוספת המשתמש');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>הוספת משתמש חדש לבניין</h2>
          <button
            type="button"
            onClick={onCancel}
            className={styles.closeButton}
            aria-label="סגור"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              <User size={20} />
              פרטים אישיים
            </h3>

            <div className={styles.formGroup}>
              <label htmlFor="FullName" className={styles.required}>
                שם מלא
              </label>
              <input
                type="text"
                id="FullName"
                name="FullName"
                value={formData.FullName}
                onChange={handleInputChange}
                className={errors.FullName ? styles.errorInput : ''}
                placeholder="הכנס שם מלא"
                disabled={loading}
              />
              {errors.FullName && (
                <span className={styles.errorMessage}>{errors.FullName}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="userEmailField" className={styles.required}>
                <Mail size={16} />
                דואר אלקטרוני
              </label>
              <input
                type="email"
                id="userEmailField"
                name="Email"
                value={formData.Email}
                onChange={handleInputChange}
                className={errors.Email ? styles.errorInput : ''}
                placeholder="example@email.com"
                autoComplete="new-password"
                data-form-type="other"
                data-lpignore="true"
                disabled={loading}
              />
              {errors.Email && (
                <span className={styles.errorMessage}>{errors.Email}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="Phone" className={styles.required}>
                <Phone size={16} />
                טלפון
              </label>
              <input
                type="tel"
                id="Phone"
                name="Phone"
                value={formData.Phone}
                onChange={handleInputChange}
                className={errors.Phone ? styles.errorInput : ''}
                placeholder="050-1234567"
                disabled={loading}
              />
              {errors.Phone && (
                <span className={styles.errorMessage}>{errors.Phone}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="PasswordHash">
                <Lock size={16} />
                סיסמה (אופציונלי)
              </label>
              <input
                type="password"
                id="PasswordHash"
                name="PasswordHash"
                value={formData.PasswordHash}
                onChange={handleInputChange}
                placeholder="השאר ריק ליצירת סיסמה אוטומטית"
                disabled={loading}
              />
              <small className={styles.fieldHint}>
                אם לא תוזן סיסמה, המערכת תיצור סיסמה זמנית אוטומטית
              </small>
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              <Shield size={20} />
              הרשאות משתמש
            </h3>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="IsManager"
                  checked={formData.IsManager === 'כן'}
                  onChange={handleInputChange}
                  disabled={true}
                />
                <Shield size={18} />
                <span>מנהל</span>
              </label>
            </div>

            <div className={styles.rolesInfo}>
              <p className={styles.infoText}>
                <strong>הרשאה:</strong>
              </p>
              <ul className={styles.rolesList}>
                <li>• מנהל - גישה מלאה למערכת</li>
              </ul>
            </div>
          </div>

          <div className={styles.formActions}>
            <button
              type="button"
              onClick={onCancel}
              className={styles.cancelButton}
              disabled={loading}
            >
              ביטול
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'שומר...' : 'הוסף משתמש'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SimpleUserForm;